import { PropsWithChildren } from 'react'

import { Column, ContainerProps, Row } from './FlexContainer'

/*
    The components in this file should only be used to create screens.
    For content layout and other general purpose layout see
        `FlexContainer.tsx`

    The layout hierarchy is explained visually below.
    Ideally, there should only be one layout column per screen.

    X  = LayoutColumnContainer
    |- = LayoutRowContainer
    w  = ContentColumn

    XXXXXXXXXXXXXXXXXXX
    X   -----------   X
    X | www www www | X
    X | www www www | X
    X | www www www | X
    X   -----------   X
    X   -----------   X
    X | wwwwwww www | X
    X | wwwwwww www | X
    X | wwwwwww www | X
    X   -----------   X
    X   -----------   X
    X | wwwwwwwwwww | X
    X | wwwwwwwwwww | X
    X | wwwwwwwwwww | X
    X   -----------   X
    XXXXXXXXXXXXXXXXXXX

    NOTE: This approach doesn't support consistent masonry.
 */

const COLUMNS = 3
const COLUMN_WIDTH = 100 / COLUMNS
const LAYOUT_CONTAINER_GAP = 2

interface LayoutContainerProps extends ContainerProps {
    className?: string
}

/**
 * Layout container arranging children in a column layout.
 * Typically, this container is the top-level page-column.
 */
export const LayoutColumnContainer = (props: LayoutContainerProps) => (
    <Column grow gap={LAYOUT_CONTAINER_GAP} {...props} className={`layout ${props.className}`} />
)

/**
 * Layout container arranging children in a row layout to create separation between vertical sections.
 * Typically, one or more of these containers are placed inside a LayoutColumnContainer.
 */
export const LayoutRowContainer = (props: LayoutContainerProps) => (
    <Row grow gap={LAYOUT_CONTAINER_GAP} {...props} className={`layout ${props.className}`} />
)

/**
 * Layout container arranging children in a flowing grid.
 * Typically, this is is used as an alternative to LayoutRowContainer
 *  to use when the number of children that will appear in a row is unknown.
 */
export const LayoutGridContainer = (props: LayoutContainerProps) => (
    <Row
        gap={LAYOUT_CONTAINER_GAP}
        style={{ flexFlow: 'row wrap', marginRight: `-${LAYOUT_CONTAINER_GAP}rem` }}
        {...props}
    />
)

interface ContentColumnProps extends PropsWithChildren {
    columns: number
}

/**
 * Content column used to layout a column inside a row container.
 *
 * NOTE: There is no "ContentRow"
 *  This is because the content layout is only concerned with overall layout from top to bottom.
 *  To display content in rows, use the generic flex container <Row>
 */
export const ContentColumn = ({ columns, children }: ContentColumnProps) => (
    <Column
        grow
        arrangement="start"
        gap={LAYOUT_CONTAINER_GAP}
        style={{ maxWidth: `${COLUMN_WIDTH * columns}%`, flex: columns }}
        children={children}
    />
)

/**
 * Content cell used to layout a dynamic number of children inside a ContentGridContainer
 *  without having to declare how the columns are seperated into rows.
 */
export const ContentCell = ({ columns, children }: ContentColumnProps) => {
    const width = COLUMN_WIDTH * columns
    const cellWidth = `calc(${width}% - ${LAYOUT_CONTAINER_GAP}rem)`
    return (
        <Column
            grow
            gap={LAYOUT_CONTAINER_GAP}
            style={{
                maxWidth: `${cellWidth}`,
                flex: `${columns} ${cellWidth}`,
            }}
            children={children}
        />
    )
}

/**
 * Standalone column container that fills the whole container space.
 * NOTE: This component is very generic and doesn't really allow any other layout in the view.
 *  For this reason, the single-column container should be used sparingly if at all.
 */
export const SingleContentColumn = ({ columns, children }: ContentColumnProps) => (
    <LayoutColumnContainer>
        <LayoutRowContainer>
            <ContentColumn columns={columns}>{children}</ContentColumn>
        </LayoutRowContainer>
    </LayoutColumnContainer>
)
